import {
    CompanySettings, DateRange, LicenseTester, LogManager, ModelRow, Navigation, StringUtil, UrlUtil, WindowTitle
} from "@mcleod/core";
import {
    Button, ClickEvent, Component, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceRemoteDataChangeEvent, KeyHandlerGroupRefresher, ModelDataChangeType, Panel, SelectionEvent, Snackbar,
    Tab, Table, TableRowCreationEvent, Toast
} from "@mcleod/components";
import { getDispatchControlBoolean, LMEUserSettings } from "@mcleod/general";
import { BrokerageMovementTableType } from "./BrokerageMovementTableType";
import { BrokerageMovementsGridExpansion } from "./BrokerageMovementsGridExpansion";
import { BrokeragePlanningPeriscope } from "./BrokeragePlanningPeriscope";
import {
    BrokerageMovementTable, BrokeragePlanningFilter, instanceOfBrokerageMoveTable, tableMinuteUpdate
} from "./BrokeragePlanningUtil";
import { AutogenLayoutBrokeragePlanning } from "./autogen/AutogenLayoutBrokeragePlanning";
import { ModelBrokeragePlanningProfile, RowBrokeragePlanningProfile } from "./models/ModelBrokeragePlanningProfile";
import { isIntercompanyEnabled } from "@mcleod/datafusion/src/EdiUtility";
import { ModelCarrierInsCtrl } from "./models/ModelCarrierInsCtrl";

interface BrokeragePlanningTab {
    type: BrokerageMovementTableType;
    layout?: BrokeragePlanningPeriscope | BrokerageMovementTable;
    tab?: Tab;
}

const log = LogManager.getLogger("lme.powerbroker.BrokeragePlanning");

export class BrokeragePlanning extends AutogenLayoutBrokeragePlanning {
    private _profileRow: RowBrokeragePlanningProfile;
    private filterObject: BrokeragePlanningFilter;
    private showingErrorToast: boolean;
    private arrivedAtShipper: boolean;
    private missingNextCheckCall: boolean;
    private noDriverInfoToday: boolean;
    private noDriverInfoTomorrow: boolean;
    private noDriverInfoTotal: boolean;
    private shipmentStatusError: boolean;
    private dataLoadActionId: string;
    private excludeBuyToMarket: boolean;
    private spot: string;
    private _brokerageProfile: string;
    private timerId: number;
    private intercompany: boolean;
    private static hasSetupHighwayFilters = false;

    private tabs: BrokeragePlanningTab[] = [
        { type: BrokerageMovementTableType.PERISCOPE },
        { type: BrokerageMovementTableType.AVAILABLE },
        { type: BrokerageMovementTableType.COVERED },
        { type: BrokerageMovementTableType.PROGRESS },
        { type: BrokerageMovementTableType.DELIVERED },
        { type: BrokerageMovementTableType.HOLD }
    ];

    override async onLoad() {
        const isEdiLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.edi");
        if (!isEdiLicensed) {
            this.removeShipmentStatusErrorButton();
        }
        if (window.location.pathname === "/lme/powerbroker/BrokeragePlanning") {
            await Navigation.navigateTo("lme/powerbroker/BrokeragePlanningMultiProfile" + window.location.search);
        }
        this.applyUrlParams();
        await this.setupHighwayFilters();
        this.setupTables();
        this.showDynamicFilters();
        this.setMinuteTimer();
        this.addUnmountListener(() => this.clearMinuteTimer());
        const partialShipmentmentEnabled = CompanySettings.get().partial_shipment_enabled;
        const partialShipmentLicense = CompanySettings.get().partial_shipment_licensed;
        this.buttonPartialShipmentPlanningScreen.visible = partialShipmentLicense && partialShipmentmentEnabled;
        const components = [
            this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id),
            this.layoutProgress.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id),
            this.layoutDelivered.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id),
            this.layoutAvailable.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id),
            this.layoutHold.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id),

        ];

        if (await isIntercompanyEnabled()) {
            components.forEach(component => {
                if (component) {
                    component.visible = true;
                }
            });
        }
        else {

            components.forEach(component => {
                if (component) {
                    component.visible = false;
                }
            });
        }
        KeyHandlerGroupRefresher.refreshOutermostLayout(this);
    }

    get brokerageProfile() {
        return this._brokerageProfile;
    }

    set brokerageProfile(value: string) {
        this._brokerageProfile = value;
    }

    private applyUrlParams() {
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        this.arrivedAtShipper = urlParams?.["arrivedAtShipper"];
        this.missingNextCheckCall = urlParams?.["missingNextCheckCall"];
        this.noDriverInfoToday = urlParams?.["noDriverInfoToday"];
        this.noDriverInfoTomorrow = urlParams?.["noDriverInfoTomorrow"];
        this.noDriverInfoTotal = urlParams?.["noDriverInfoTotal"];
        this.brokerageProfile = urlParams?.["bpProfile"] ?? this.brokerageProfile;
        this.shipmentStatusError = urlParams?.["shipmentStatusError"];
        this.dataLoadActionId = urlParams?.["dataLoadActionId1"];
        this.excludeBuyToMarket = urlParams?.["excludeBuyToMarket"];
        this.spot = urlParams?.["spot"];
        this.intercompany = urlParams?.["intercompany"];
    }

    private showDynamicFilters() {
        const buttonNoDriverInfoToday = this.layoutCovered?.findComponentById('btn' + BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TODAY.id);
        if (buttonNoDriverInfoToday != null) {
            buttonNoDriverInfoToday.visible = !!this.noDriverInfoToday;
        }
        const buttonNoDriverInfoTomorrow = this.layoutCovered?.findComponentById('btn' + BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TOMORROW.id);
        if (buttonNoDriverInfoTomorrow != null) {
            buttonNoDriverInfoTomorrow.visible = !!this.noDriverInfoTomorrow;
        }
        const buttonNoDriverInfoTotal = this.layoutCovered?.findComponentById('btn' + BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TOTAL.id);
        if (buttonNoDriverInfoTotal != null) {
            buttonNoDriverInfoTotal.visible = !!this.noDriverInfoTotal;
        }

        const buttonExcludeBTMCovered = this.layoutCovered?.findComponentById('btn' + BrokerageMovementTableType.FILTER_EXCLUDE_BUY_TO_MARKET.id);
        if (buttonExcludeBTMCovered != null) {
            buttonExcludeBTMCovered.visible = !!this.excludeBuyToMarket;
        }
        const buttonExcludeBTMProgress = this.layoutProgress?.findComponentById('btn' + BrokerageMovementTableType.FILTER_EXCLUDE_BUY_TO_MARKET.id);
        if (buttonExcludeBTMProgress != null) {
            buttonExcludeBTMProgress.visible = !!this.excludeBuyToMarket;
        }
        const buttonExcludeBTMDelivered = this.layoutDelivered?.findComponentById('btn' + BrokerageMovementTableType.FILTER_EXCLUDE_BUY_TO_MARKET.id);
        if (buttonExcludeBTMDelivered != null) {
            buttonExcludeBTMDelivered.visible = !!this.excludeBuyToMarket;
        }
    }

    async setupHighwayFilters() {
        if (BrokeragePlanning.hasSetupHighwayFilters) {
            return; // Skip if already called
        }
        const response = await new ModelCarrierInsCtrl().search();
        const highwayRow = response.modelRows.find(row => row.get("vendor_id") === "H");
        if (highwayRow) {
            const isActiveVendor: boolean = StringUtil.equalsIgnoreCase(highwayRow.get("is_active"), "Y");
            const isHighwaySendLoads: boolean = StringUtil.equalsIgnoreCase(highwayRow.get("send_load_data"), "Y");
            const isCreatingOrderHistAlerts: boolean = StringUtil.equalsIgnoreCase(highwayRow.get("add_hwy_lla_to_order_hist"), "Y");

            if (isActiveVendor && isHighwaySendLoads && isCreatingOrderHistAlerts) {
                BrokerageMovementTableType.COVERED.secondaryFilters = BrokerageMovementTableType.COVERED.secondaryFilters || [];
                BrokerageMovementTableType.PROGRESS.secondaryFilters = BrokerageMovementTableType.PROGRESS.secondaryFilters || [];
                BrokerageMovementTableType.COVERED.secondaryFilters.push(BrokerageMovementTableType.LOAD_LOCK_ALERT);
                BrokerageMovementTableType.PROGRESS.secondaryFilters.push(BrokerageMovementTableType.LOAD_LOCK_ALERT);
            }
        }
        BrokeragePlanning.hasSetupHighwayFilters = true;
    }

    setupTables() {
        for (let i = 0; i < this.tabs.length; i++) {
            const tab = this.tabsetMain.components.find((component: Tab) => component.caption === `${this.tabs[i].type.caption}`) as Tab;
            const layout = tab?.components[0];
            if (layout instanceof BrokeragePlanningPeriscope || instanceOfBrokerageMoveTable(layout)) {
                layout.configureTableForType(this.tabs[i].type);
                this.tabs[i].layout = layout;
                this.tabs[i].tab = tab;
                layout.brokeragePlanning = this;
                layout.tableMovements.addRowCreateListener((event: TableRowCreationEvent) => event.getTable().busy = false);
            }
        }
    }

    public get profileRow(): RowBrokeragePlanningProfile {
        return this._profileRow;
    }

    public setProfileRow(value: ModelRow) {
        if (value != this._profileRow) {
            this.textBoxDateRange.visible = false;
            this._profileRow = value;
            this.filterObject = new BrokeragePlanningFilter(value);
            if (this._profileRow != null) {
                this.searchMainDatasource();
                this.textBoxDateRange.text = DateRange.formatWithDaysOut(this._profileRow.get("start_day"), this._profileRow.get("order_days"));
                this.textBoxDateRange.visible = !this.textBoxDateRange.isEmpty();
            }
        }
    }

    private setSecondaryFilters() {
        if (this.arrivedAtShipper) {
            this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_ARRIVED_AT_SHIPPER.id).clicked();
        }
        else if (this.missingNextCheckCall) {
            this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_MISS_NEXT_CALL_DATE.id).clicked();
            this.layoutProgress.findComponentById('btn' + BrokerageMovementTableType.FILTER_MISS_NEXT_CALL_DATE.id).clicked();
        }
        else if (this.noDriverInfoToday) {
            this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TODAY.id).clicked();
        }
        else if (this.noDriverInfoTomorrow) {
            this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TOMORROW.id).clicked();
        }
        else if (this.noDriverInfoTotal) {
            this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TOTAL.id).clicked();
        }
        else if (this.excludeBuyToMarket && getDispatchControlBoolean('show_btm')) {
            const currentTab = this.tabs.find(tab => tab.tab.selected).tab;
            const excludeBuyToMarketButton = currentTab.findComponentById('btn' + BrokerageMovementTableType.FILTER_EXCLUDE_BUY_TO_MARKET.id) as Button;
            if (excludeBuyToMarketButton != null) {
                excludeBuyToMarketButton.visible = true;
                excludeBuyToMarketButton.clicked();
            }
        }
        else if (this.spot === "include") {
            this.layoutAvailable.findComponentById('btn' + BrokerageMovementTableType.FILTER_SPOT_ORDERS.id).clicked();
        }
        else if (this.spot === "exclude") {
            this.layoutAvailable.findComponentById('btn' + BrokerageMovementTableType.FILTER_COMMITTED_ORDERS.id).clicked();
        }
        else if (this.shipmentStatusError) {
            switch (this.dataLoadActionId) {
                case "tabAvailableOrders": {
                    this.layoutAvailable.findComponentById('btn' + BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR.id).clicked();
                    break;
                }
                case "tabCoveredOrders": {
                    this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR.id).clicked();
                    break;
                }
                case "tabDeliveredOrders": {
                    this.layoutDelivered.findComponentById('btn' + BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR.id).clicked();
                    break;
                }
                case "tabInProgressOrders": {
                    this.layoutProgress.findComponentById('btn' + BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR.id).clicked();
                    break;
                }
            }
        } else if (this.intercompany) {
            switch (this.dataLoadActionId) {
                case "tabAvailableOrders": {
                    this.layoutAvailable.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id).clicked();
                    break;
                }
                case "tabCoveredOrders": {
                    this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id).clicked();
                    break;
                }
                case "tabDeliveredOrders": {
                    this.layoutDelivered.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id).clicked();
                    break;
                }
                case "tabInProgressOrders": {
                    this.layoutProgress.findComponentById('btn' + BrokerageMovementTableType.FILTER_INTERCOMPANY.id).clicked();
                    break;
                }
            }
        }

        if (!this.mainDataSource.activeRow?.getBoolean("tracking_vendor_active")) {
            const componentsToHide = [
                this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.TRACKING_ALERT.id),
                this.layoutCovered.findComponentById('btn' + BrokerageMovementTableType.TRACKING_REQUIRED.id),
                this.layoutProgress.findComponentById('btn' + BrokerageMovementTableType.TRACKING_ALERT.id),
                this.layoutProgress.findComponentById('btn' + BrokerageMovementTableType.TRACKING_REQUIRED.id),
                this.layoutDelivered.findComponentById('btn' + BrokerageMovementTableType.TRACKING_ALERT.id)
            ];

            componentsToHide.forEach(component => {
                if (component) {
                    component.visible = false;
                }
            });
        }

    }

    // called from MultiProfilePlanningTabs.ts
    async loadProfile(profile?: string | ModelRow) {
        log.debug("Loading profile")
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? this.brokerageProfile ?? LMEUserSettings.get()?.bp_profile;
        if (profileId == null)
            Snackbar.showSnackbar("You do not have a default brokerage planning profile defined. "
                + "Add a default profile to your preferences so your orders will automatically "
                + "display or you can select a profile above to get started.", { persist: true });
        else {
            const profileRow = await new ModelBrokeragePlanningProfile().searchSingle({ id: profileId });
            if (profileRow == null)
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            else
                this.setProfileRow(profileRow);
        }
    }

    searchMainDatasource() {
        this.mainDataSource.search({
            profile_row: this.profileRow
        }).then(() => this.setSecondaryFilters());
    }

    getTabAndFilterCounts() {
        const countMap = {};
        const tabIndexMap = {};
        this.tabs.forEach((tab, index) => {
            countMap[tab.type.tableIdSuffix] = {};
            if (tab.type.moveStatus)
                tabIndexMap[tab.type.moveStatus] = index;
            else
                tabIndexMap[tab.type.tableIdSuffix] = index;
        })

        this.mainDataSource.data.forEach(row => {
            const types: BrokerageMovementTableType[] = [];
            if (row.get("move_status") in tabIndexMap) {
                types.push(this.tabs[tabIndexMap[row.get("move_status")]].type);
            }

            if (row.get("hold") === true) {
                types.push(this.tabs[tabIndexMap["OnHold"]].type);
            }

            types.forEach((type: BrokerageMovementTableType) => {
                const passedHoldFilterCheck = BrokerageMovementTableType.holdFilter(row, this.profileRow);
                if (!passedHoldFilterCheck && type != BrokerageMovementTableType.HOLD) {
                    return;
                }
                if (!countMap[type.tableIdSuffix]["totalCount"]) {
                    countMap[type.tableIdSuffix]["totalCount"] = 1;
                } else {
                    countMap[type.tableIdSuffix]["totalCount"] += 1;
                }

                if (type.secondaryFilters) {
                    type.secondaryFilters.forEach(sFilter => {
                        if (!countMap[type.tableIdSuffix][sFilter.caption])
                            countMap[type.tableIdSuffix][sFilter.caption] = { id: sFilter.id, caption: sFilter.caption, totalCount: 0 };
                        if (sFilter.filter(row)) {
                            countMap[type.tableIdSuffix][sFilter.caption]["totalCount"] += 1;
                        }
                    })
                }
            })
        })

        return countMap;
    }

    override get windowTitle() {
        const profileId = this.profileRow?.getString("id");
        if (profileId) {
            const count = this.mainDataSource?.data?.length;
            if (count)
                return `${profileId} (${count})`;
            return profileId;
        }
        return "Brokerage Planning";
    }

    sourceBrokerageMovementsBeforeExecution(event: DataSourceExecutionEvent) {
        if (DataSourceAction.SEARCH == event.getAction()) {
            this.tabs.forEach(tab => {
                if (tab.layout != null) {
                    tab.layout.tableMovements.busy = true
                }
            });
        }
    }

    sourceBrokerageMovementsAfterExecution(event) {
        if (DataSourceAction.SEARCH == event.getAction()) {
            this.updateTabTitles();
            this.populateFilteredTables();
        }
    }

    populateFilteredTables(tableType?: BrokerageMovementTableType) {
        this.tabs.forEach((tab, index) => {
            if ((tableType != null && tableType != tab.type) || tab.layout == null)
                return;

            this.populateFilteredTable(tab, index);
        })
    }

    populateFilteredTable(tab: BrokeragePlanningTab, index: number) {
        const layout = tab.layout;
        const modelRows = this.sourceBrokerageMovements.data.filter(row => layout.type.filter(row, this.profileRow));
        layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, modelRows, null);
        if (instanceOfBrokerageMoveTable(layout)) {
            layout.layoutBrokeragePlanningToolPanel.isContractManagementActive = this.sourceBrokerageMovements.summaryData.getBoolean("is_carrier_connect_active", false);
        }
        if (index != this.tabsetMain.selectedIndex && layout.tableMovements.rowCount > 0) {
            // the following will populate up to 5 rows when the table is hidden.
            // This avoids populating them when the user switches tab
            const rowsToPopulate = Math.min(5, layout.tableMovements.rowCount)
            for (let i = 0; i < rowsToPopulate; i++)
                layout.tableMovements.rows[i]?.populateDOMIfNeeded();
        }
    }

    updateTabTitles() {
        const tabMap = this.getTabAndFilterCounts();
        this.tabs.forEach((tab, index) => {
            if (tab.layout == null) return;
            tab.layout.tableMovements.busy = false
            if (index == 0) {
                tab.tab.caption = tab.type.caption + " " + this.mainDataSource.data.length;
            } else {
                const tabCountMap = tabMap[tab.type.tableIdSuffix];
                tab.tab.caption = tab.type.caption + " " + (tabCountMap.totalCount ?? 0);
                if (Object.keys(tabCountMap).length > 0) {
                    for (const value in tabCountMap) {
                        if (value != "totalCount") {
                            const movementTable = tab.layout as BrokerageMovementTable;
                            const btn = movementTable.panelSecondaryFilters.findComponentById(`btn${tabCountMap[value].id}`) as Button;
                            btn.caption = `${tabCountMap[value].caption} ${tabCountMap[value].totalCount}`;
                        }
                    }
                }
                else {
                    (tab.layout as BrokerageMovementTable).panelSecondaryFilters.components.forEach((btn: Button) => {
                        btn.caption = `${btn.allProps.caption} 0`;
                    });
                }
            }
        });
        WindowTitle.refresh("Brokerage Planning (TabTitles)");
    }

    buttonCarrierCapacityOnClick(event: ClickEvent) {
        Navigation.navigateTo("lme/powerbroker/CarrierCapacity", { newTab: true, height: 1400, width: 1850, windowDecorators: false });
    }

    /** This is an event handler for the onRemoteDataChange event of sourceBrokerageMovements.  */
    sourceBrokerageMovementsOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let eventChangeType = event.change.type;
        if (eventChangeType != ModelDataChangeType.DELETE && !this.filterObject.dataPasses(event.change.data))
            eventChangeType = ModelDataChangeType.DELETE;
        const changedRow = event.change.data as ModelRow;

        let changedValues = {};
        if (eventChangeType == ModelDataChangeType.UPDATE && changedRow != null) {
            changedValues = changedRow.compareWithData(changedRow.get("originalData"));
            log.debug("timestamp " + event.change.lastUpdateNanos)
        }

        for (let i = 0; i < this.tabs.length; i++) {
            let changeType = eventChangeType;
            if (changeType != ModelDataChangeType.DELETE)
                changeType = this.tabs[i].layout.type.filter(changedRow, this.profileRow) ? event.change.type : ModelDataChangeType.DELETE;
            const changeForTable = { type: changeType, data: changedRow, lastUpdateNanos: event.change.lastUpdateNanos };
            this.tabs[i].layout.mainDataSource.handleDataChange(changeForTable);
            if (this.tabs[i].type == BrokerageMovementTableType.AVAILABLE && changedValues["waterfall_in_progress"] != null) {
                this.handleCarrierOfferLiveUpdate(changedRow, this.tabs[i].layout as BrokerageMovementTable);
            }
        }
        this.mainDataSource.handleDataChange({ type: eventChangeType, data: changedRow, lastUpdateNanos: event.change.lastUpdateNanos });

        /**
         * Typically dataSource updates happen automatically on reDisplayRow, but in this case there are no bound components.
         * If handleDataChange() in the future handles this case, the following code can be removed.
         */
        if (eventChangeType == ModelDataChangeType.UPDATE) {
            const movementId = changedRow.get("movement_id");
            const existingRow: boolean = this.mainDataSource.data.some(row => row.get("movement_id") === movementId);
            if (!existingRow) {
                this.mainDataSource.addRow(changedRow);
            }
        }
        this.updateTabTitles();
        event.preventDefault();
    }

    /** This is an event handler for the onSubscriberError event of sourceBrokerageMovements.  */
    sourceBrokerageMovementsOnSubscriberError(event) {
        log.error(event.error);
        if (!this.showingErrorToast) {
            this.showingErrorToast = true;
            Toast.showToast("There was a problem keeping this page up-to-date real time.  Try refreshing the page.", null, { persist: true, onDismiss: () => this.showingErrorToast = false });
        }
    }

    async handleCarrierOfferLiveUpdate(changedRow: ModelRow, tableLayout: BrokerageMovementTable) {
        const changedTableRow = tableLayout.tableMovements.rows.find(tableRow => tableRow.data.get("movement_id") === changedRow.get("movement_id"));
        if (changedTableRow && changedTableRow.expanded) {
            const expandedRow = await changedTableRow.getExpansionComponentLoaded() as Panel;
            const gridExpansion = expandedRow.findComponentById("ExpandedBrokerageMovementsGrid") as BrokerageMovementsGridExpansion;
            const waterfallInProgress = changedRow.get("waterfall_in_progress", false);
            if (waterfallInProgress) {
                await gridExpansion.layoutMovementOfferTabs.layoutWaterfallOffers.search();
            }
            gridExpansion.layoutMovementOfferTabs.updateWaterfallComponents(waterfallInProgress);
        }
    }

    /** This is an event handler for the afterTabSelection event of tabsetMain.  */
    tabsetMainAfterTabSelection(event: SelectionEvent) {
        if (event.newSelection instanceof Tab) {
            const table = event.newSelection.findComponentById((component: Component) => component.id?.startsWith("tableMovements") === true) as Table;
            if (table != null) {
                table.scrollToSelection();
                table.setRowsExpanded(false);
            }
        }
    }

    // START - Minute updater stuff
    private setMinuteTimer() {
        this.timerId = window.setTimeout(() => this.executeMinuteUpdates(), (60 - new Date().getSeconds()) * 1000);
    }

    private clearMinuteTimer() {
        log.debug("Clearing minute timer")
        clearTimeout(this.timerId);
        this.timerId = null;
    }

    private async executeMinuteUpdates() {
        this.tabs.forEach(tab => tab.layout != null && tableMinuteUpdate(tab.layout as BrokerageMovementTable))
        this.setMinuteTimer();
    }
    // END - Minute updater stuff

    buttonPartialShipmentPlanningScreenOnClick() {
        Navigation.navigateTo("lme/dispatch/PartialShipmentPlanning", { newTab: true });
    }

    removeShipmentStatusErrorButton() {
        this.tabs.forEach(tab => {
            if (tab?.type?.secondaryFilters) {
                tab.type.secondaryFilters = tab.type.secondaryFilters?.filter(filter => filter.id !== BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR.id);
            }
        })
    }
}
